import { useTranslations } from 'next-intl';
import HighlightText from '@atoms/HighlightText/HighlightText';
import Label from '@atoms/Label/Label';
import IconTooltip from '@molecules/IconTooltip/IconTooltip';
import ScreenReaderMessage from '@atoms/ScreenReaderMessage/ScreenReaderMessage';
import Text from '@atoms/Text/Text';
import Info from '@icons/info.svg';
import { StyledPickupLocationListItem, StyledStoreInfoContainer } from './PickUpLocationListItem.styles';

type AddressType = {
  town: string;
  line1: string;
  postalCode: string;
};

// The API returns displayName but @occ/api-client's types don't include it
export type SuggestionType = {
  displayName: string;
  storeId: string;
  address: AddressType;
  externalPickupLocation: string;
  franchise: string;
  name?: string;
  geoPoint?: { latitude: number; longitude: number };
};

export type OnItemClick = (suggestion: SuggestionType) => void;

interface Props {
  suggestion: SuggestionType;
  searchTerm: string;
  onItemClick?: OnItemClick;
  isOrderReopened?: boolean;
  selectedStore?: string;
}

const PickupLocationListItem = ({ suggestion, searchTerm, onItemClick, isOrderReopened, selectedStore }: Props) => {
  const t = useTranslations('deliverypicker');
  const tCommon = useTranslations('common');
  const { displayName, address, externalPickupLocation, franchise } = suggestion;
  const formattedPostalCode = address.postalCode
    ? `${address.postalCode.slice(0, 3)} ${address.postalCode.slice(3, 5)}`
    : '';
  const isSelectable = !(isOrderReopened && !!franchise && selectedStore !== suggestion.storeId);

  return (
    <StyledPickupLocationListItem
      data-testid="pickup-location-list-item"
      data-disabled={!isSelectable}
      $disabled={!isSelectable}
      onClick={() => {
        if (onItemClick && isSelectable) onItemClick(suggestion);
      }}
    >
      {externalPickupLocation && (
        <Label variant="mixAndMatch">{t('autoComplete.pickupLocation.labels.externalLocation')}</Label>
      )}
      <StyledStoreInfoContainer>
        {/* needed for flex */}
        <div>
          <Text type="subhead">
            <HighlightText disabled={!isSelectable} text={displayName} term={searchTerm} />
          </Text>

          <Text type="body" size="small">
            <HighlightText disabled={!isSelectable} text={`${address.line1}, `} term={searchTerm} />
            <HighlightText disabled={!isSelectable} text={`${formattedPostalCode} ${address.town}`} term={searchTerm} />
          </Text>
        </div>

        {!isSelectable && (
          <>
            <ScreenReaderMessage>{t('autoComplete.pickupLocation.franchise.storeNotChangeable')}</ScreenReaderMessage>

            <IconTooltip
              align="right"
              position="row-left"
              content={t('autoComplete.pickupLocation.franchise.storeNotChangeable')}
              iconProps={{ svg: Info, size: 16, 'aria-label': tCommon('aria.infoIcon') }}
            />
          </>
        )}
      </StyledStoreInfoContainer>
    </StyledPickupLocationListItem>
  );
};

export default PickupLocationListItem;
